import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sofa, Bed, Package, Bike, WashingMachine, Tv, Car, Refrigerator, Wrench, FileText, Briefcase, Armchair, Home } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_BY_LABEL = (label) => {
  const k = (label || '').toLowerCase();
  if (k.includes('sofa') || k.includes('couch')) return Sofa;
  if (k.includes('house') || k.includes('home move')) return Home;
  if (k.includes('bed') || k.includes('mattress')) return Bed;
  if (k.includes('wardrobe') || k.includes('furniture')) return Armchair;
  if (k.includes('washing') || k.includes('washer')) return WashingMachine;
  if (k.includes('fridge') || k.includes('appliance')) return Refrigerator;
  if (k.includes('tv') || k.includes('television')) return Tv;
  if (k.includes('bike') || k.includes('bicycle')) return Bike;
  if (k.includes('vehicle') || k.includes('car')) return Car;
  if (k.includes('tool') || k.includes('equipment')) return Wrench;
  if (k.includes('doc')) return FileText;
  if (k.includes('parcel') || k.includes('box') || k.includes('package')) return Package;
  return Briefcase;
};

const VERB_LABEL = {
  delivered: 'delivered',
  collected: 'collected',
  booked: 'booked',
};

export const LiveActivityStrip = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/live-activity`);
        if (!cancelled) setItems(res.data.items || []);
      } catch {
        /* silent — landing page must not break if API is slow */
      }
    };
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!items.length) return null;

  return (
    <section className="bg-[#1B5E20] text-white" data-testid="live-activity-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          {/* Header */}
          <div className="flex-shrink-0 md:max-w-[200px]">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
              </span>
              <span className="text-xs uppercase tracking-widest font-bold">LIVE</span>
            </div>
            <h3
              className="mt-1 text-lg md:text-xl font-bold leading-tight"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Live Delivery Activity
            </h3>
            <p className="text-[11px] md:text-xs text-emerald-100 mt-0.5">
              Real deliveries happening across Ireland right now
            </p>
          </div>

          {/* Horizontal scroll row */}
          <div className="flex-1 overflow-hidden">
            <ul
              className="flex gap-4 md:gap-6 overflow-x-auto md:overflow-x-hidden pb-1 snap-x snap-mandatory md:snap-none scrollbar-hide [animation-play-state:paused] md:[animation-play-state:running]"
              style={{ scrollbarWidth: 'none' }}
              aria-label="Live delivery activity"
            >
              {items.map((it, i) => {
                const Icon = ICON_BY_LABEL(it.label);
                const verb = VERB_LABEL[it.verb] || 'delivered';
                return (
                  <li
                    key={`${i}-${it.at}`}
                    className="flex-shrink-0 snap-start min-w-[180px] md:min-w-[170px]"
                    data-testid={`live-activity-item-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-9 h-9 rounded-md bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-white" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold leading-tight">
                          {it.label} {verb}
                        </p>
                        <p className="text-xs text-emerald-100 leading-snug">
                          today in <span className="font-medium">{it.county}</span>
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveActivityStrip;
